<template>
  <v-container fluid class="ingredients-page">
    <section>
      <v-card outlined class="rounded-xl ingredients-shell">
        <v-card-text class="pa-4 pa-md-6">
          <div v-if="showIngredientsSkeleton">
            <div class="ingredients-skeleton-toolbar">
              <div class="skeleton-block skeleton-title-block"></div>
              <div class="ingredients-skeleton-actions">
                <div class="skeleton-block skeleton-btn"></div>
                <div class="skeleton-block skeleton-btn"></div>
              </div>
            </div>

            <div class="ingredients-skeleton-chips mt-4">
              <v-skeleton-loader
                v-for="index in 5"
                :key="`status-skeleton-${index}`"
                type="chip"
                width="84"
              />
            </div>

            <div class="ingredients-skeleton-filters mt-4">
              <v-skeleton-loader type="text" width="260" />
              <v-skeleton-loader type="text" width="220" />
            </div>
          </div>

          <template v-else>
            <div class="ingredients-header">
              <div>
                <div class="ingredients-title">{{ $t("ingredients") }}</div>
                <div class="ingredients-subtitle">
                  {{ $t("ingredient_page_subtitle") }}
                </div>
              </div>
              <!-- Both buttons lead to endpoints the API gates on
                   manage_product_recipe (IngredientController::store/update/
                   destroy and IngredientRestockController::store all call
                   roleCheck on it). Offering them to a user without the grant
                   only bought them a permission_not_found popup. -->
              <div v-if="canManage" class="ingredients-header-actions">
                <v-btn
                  depressed
                  outlined
                  color="primary"
                  class="rounded-pill text-none ingredients-action-btn"
                  @click="goRestock"
                >
                  <v-icon left small>fal fa-dolly</v-icon>
                  {{ $t("restock") }}
                </v-btn>
                <v-btn
                  depressed
                  color="primary"
                  class="white--text rounded-pill text-none ingredients-action-btn"
                  @click="newItem"
                >
                  <v-icon left small>fal fa-plus-circle</v-icon>
                  {{ $t("new_data") }}
                </v-btn>
              </div>
            </div>

            <div class="ingredients-filters-row">
              <div class="ingredients-filter-group">
                <span class="ingredients-filter-label">{{ $t("status") }}</span>
                <div class="br-tabs">
                  <div
                    v-for="(item, i) in statusList"
                    :key="i"
                    class="br-tab"
                    :class="{ 'br-tab--active': status_filter === item.value }"
                    @click="status_filter = item.value"
                  >
                    {{ $t(item.text) }}
                  </div>
                </div>
              </div>

              <div class="ingredients-filter-divider"></div>

              <div class="ingredients-filter-group">
                <span class="ingredients-filter-label">{{ $t("stock") }}</span>
                <div class="br-tabs">
                  <div
                    v-for="(item, i) in stockList"
                    :key="i"
                    class="br-tab"
                    :class="{ 'br-tab--active': stock_filter === item.value }"
                    @click="stock_filter = item.value"
                  >
                    {{ $t(item.text) }}
                  </div>
                </div>
              </div>
            </div>

            <div class="ingredients-toolbar">
              <div class="ingredients-search">
                <v-icon small class="ingredients-search-icon">fal fa-search</v-icon>
                <label :for="searchInputId" class="sr-only">{{ $t("search") }}</label>
                <input
                  :id="searchInputId"
                  v-model="filters.q"
                  type="text"
                  class="ingredients-search-input"
                  :placeholder="$t('search')"
                  @keyup.enter="fetchIngredients()"
                />
                <v-btn
                  v-if="filters.q"
                  icon
                  x-small
                  class="ingredients-search-clear"
                  @click="
                    filters.q = '';
                    fetchIngredients();
                  "
                >
                  <v-icon x-small>fal fa-times</v-icon>
                </v-btn>
              </div>

              <TextSelect
                class="ingredients-supplier-select"
                searchable
                :placeholder="false"
                label="supplier"
                itemValue="id"
                text="name"
                :returnObject="false"
                :items="supplierFilterOptions"
                v-model="filters.vendor_id"
                @input="fetchIngredients()"
              />
            </div>
          </template>
        </v-card-text>
      </v-card>

      <div v-if="!showIngredientsSkeleton" class="ingredients-table-wrap mt-4">
        <data-table
          :headers="headers"
          :items="ingredients"
          commit="SET_INGREDIENTS"
          :viewItem="viewItem"
          :editItem="canManage ? editItem : null"
          :deleteItem="canManage ? deleteItem : null"
          :fetchItems="fetchIngredients"
        ></data-table>
      </div>

      <app-custom-dialog
        v-model="dialog"
        :footer="false"
        title="description"
        width="900px"
      >
        <div slot="activator" class="ingredient-detail">
          <div class="detail-header">
            <div class="detail-header-name">{{ form.name_la || "-" }}</div>
            <v-chip
              x-small
              label
              :color="form.is_active ? 'green lighten-5' : 'grey lighten-4'"
              :text-color="form.is_active ? 'green darken-2' : 'grey darken-1'"
              class="font-weight-medium"
            >
              {{ form.is_active ? $t("active") : $t("inactive") }}
            </v-chip>
          </div>

          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">{{ $t("id") }}</span>
              <span class="detail-value">{{ form.id }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ $t("code") }}</span>
              <span class="detail-value">{{ form.code || "-" }}</span>
            </div>
            <div v-if="form.name_en" class="detail-item">
              <span class="detail-label">{{ $t("name_en") }}</span>
              <span class="detail-value">{{ form.name_en }}</span>
            </div>
            <div v-if="form.name_th" class="detail-item">
              <span class="detail-label">{{ $t("name_th") }}</span>
              <span class="detail-value">{{ form.name_th }}</span>
            </div>
            <div v-if="form.name_cn" class="detail-item">
              <span class="detail-label">{{ $t("name_cn") }}</span>
              <span class="detail-value">{{ form.name_cn }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ $t("unit") }}</span>
              <span class="detail-value">{{ selectedUnitLabel(form.unit_id) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ $t("supplier") }}</span>
              <span class="detail-value">{{ selectedSupplierName(form.vendor_id) }}</span>
            </div>
          </div>

          <div class="detail-section-title">{{ $t("stock") }}</div>
          <div class="detail-stats-row">
            <div class="detail-stat">
              <div class="detail-stat-value">
                {{ numberText(form.current_stock) }}
                {{ selectedUnitLabel(form.unit_id, form) }}
              </div>
              <div class="detail-stat-label">{{ $t("current_stock") }}</div>
            </div>
            <div class="detail-stat">
              <div class="detail-stat-value">
                {{ numberText(form.minimum_stock) }}
                {{ selectedUnitLabel(form.unit_id, form) }}
              </div>
              <div class="detail-stat-label">{{ $t("minimum_stock") }}</div>
            </div>
            <div class="detail-stat">
              <div class="detail-stat-value">{{ numberText(form.unit_cost) }}</div>
              <div class="detail-stat-label">{{ $t("unit_cost") }}</div>
            </div>
            <div class="detail-stat">
              <div class="detail-stat-value">{{ numberText(bomUsage(form)) }}</div>
              <div class="detail-stat-label">{{ $t("bom_usage") }}</div>
            </div>
          </div>

          <div class="detail-section-title">{{ $t("conversion_rates") }}</div>
          <div v-if="!conversionFormulas.length" class="detail-empty">-</div>
          <div v-else class="conversion-row-list">
            <div
              v-for="formula in conversionFormulas"
              :key="formula.id"
              class="conversion-row-card"
            >
              <span class="conversion-row-unit">{{ formula.fromText }}</span>
              <v-icon x-small class="conversion-row-arrow">fal fa-arrow-right</v-icon>
              <span class="conversion-row-unit">{{ formula.toText }}</span>
              <span
                class="conversion-row-purpose"
                :class="`conversion-row-purpose--${formula.purposeKey}`"
              >
                {{ formula.purpose }}
              </span>
            </div>
          </div>
        </div>
      </app-custom-dialog>

    </section>
  </v-container>
</template>

<script>
import DataTable from "@/components/DataTable";
import TextSelect from "@/components/global/TextSelect";
import { ingredientHead } from "@/utils/head";
import { conversionFactor, stockUnitText } from "@/utils/ingredientConversion";
import { confirm, success } from "@/controllers/alert";
import { mapGetters } from "vuex";

export default {
  name: "ManageIngredientsPage",
  components: {
    DataTable,
    TextSelect,
  },
  data: () => ({
    loadingIngredients: true,
    deletingIngredient: false,
    hasLoadedIngredients: false,
    dialog: false,
    status_filter: null,
    stock_filter: "",
    filters: {
      q: "",
      vendor_id: "",
    },
    suppliers: [],
    units: [],
    form: {
      id: null,
      vendor_id: null,
      code: "",
      name_la: "",
      name_en: "",
      name_th: "",
      name_cn: "",
      unit_id: null,
      current_stock: 0,
      minimum_stock: 0,
      unit_cost: 0,
      is_active: true,
      bom_items_count: 0,
      bom_stock_logs_count: 0,
    },
  }),
  computed: {
    ...mapGetters({
      ingredients: "index/ingredients",
      user: "auth/user",
    }),
    /**
     * Whether this user may change ingredients at all — the same grant the API
     * checks before accepting a create, edit, delete or restock. A user given
     * the Ingredients menu but not this keeps the page and the view button, so
     * "sell and look at stock" stays exactly that.
     */
    canManage() {
      const menus = (this.user && this.user.auth_menus) || [];
      return menus.some(
        (item) =>
          item.code === "manage_product_recipe" ||
          (item.menu || []).some((child) => child.code === "manage_product_recipe")
      );
    },
    searchInputId() {
      return `ingredients-search-${this._uid}`;
    },
    headers() {
      return ingredientHead(this);
    },
    unitOptions() {
      return this.units.map((unit) => ({
        ...unit,
        label: this.unitLabel(unit),
      }));
    },
    supplierFilterOptions() {
      return [{ id: "", name: this.$t("all") }, ...this.suppliers];
    },
    statusList() {
      return [
        { value: null, text: "all" },
        { value: 1, text: "active" },
        { value: 0, text: "inactive" },
      ];
    },
    // "1 Pack = 100 gram", tagged with the purpose it was configured for.
    conversionFormulas() {
      return (this.form.unit_conversions || [])
        .filter((row) => row.is_active === undefined || Number(row.is_active))
        .map((row) => {
          const purposeKey = row.purpose === "usage" ? "usage" : "restock";
          return {
            id: row.id,
            purposeKey,
            purpose: this.$t(purposeKey),
            fromText: `${this.numberText(row.from_quantity)} ${this.conversionUnitLabel(
              row.from_unit,
              row.from_unit_id
            )}`,
            toText: `${this.numberText(row.to_quantity)} ${this.conversionUnitLabel(
              row.to_unit,
              row.to_unit_id
            )}`,
          };
        });
    },
    stockList() {
      return [
        { value: "", text: "all" },
        { value: "low_stock", text: "low_stock" },
      ];
    },
    showIngredientsSkeleton() {
      return this.loadingIngredients && !this.hasLoadedIngredients;
    },
  },
  watch: {
    status_filter() {
      this.fetchIngredients();
    },
    stock_filter() {
      this.fetchIngredients();
    },
  },
  async created() {
    await Promise.all([this.fetchSuppliers(), this.fetchUnits()]);
    await this.fetchIngredients();
  },
  methods: {
    async fetchIngredients(pagination = {}) {
      try {
        this.loadingIngredients = true;
        const requestUrl = pagination.url || this.ingredientsUrl();
        const { data } = await this.$http.get(requestUrl);
        if (data.status) {
          const page = {
            ...data.data,
            data: (data.data.data || []).map(this.mapIngredientForTable),
          };
          this.$store.commit("index/SET_INGREDIENTS", page);
          this.hasLoadedIngredients = true;
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.loadingIngredients = false;
      }
    },
    ingredientsUrl() {
      const params = new URLSearchParams();
      Object.keys(this.filters).forEach((key) => {
        const value = this.filters[key];
        if (value !== "" && value !== null && value !== undefined) {
          params.append(key, value);
        }
      });
      if (this.status_filter !== null && this.status_filter !== undefined) {
        params.append("is_active", this.status_filter);
      }
      if (this.stock_filter) {
        params.append("stock_filter", this.stock_filter);
      }
      return `ingredients_paginate?${params.toString()}`;
    },
    async fetchSuppliers() {
      const { data } = await this.$http.get("suppliers");
      if (data.status) {
        this.suppliers = Array.isArray(data.data) ? data.data : [];
      }
    },
    async fetchUnits() {
      const { data } = await this.$http.get("product_size");
      if (data.status) {
        this.units = Array.isArray(data.data) ? data.data : [];
      }
    },
    newItem() {
      this.$router.push("/manage-ingredients/new");
    },
    goRestock() {
      this.$router.push("/ingredient-restock");
    },
    viewItem(item) {
      this.form = this.mapIngredientToForm(item);
      this.dialog = true;
    },
    editItem(item) {
      this.$router.push(`/manage-ingredients/${item.id}`);
    },
    async deleteItem(item) {
      const { isConfirmed } = await confirm({
        props: this,
        text: "want_to_delete",
      });
      if (!isConfirmed) return;

      try {
        this.deletingIngredient = true;
        const { data } = await this.$http.delete(`ingredients/${item.id}`);
        if (data.status) {
          await this.fetchIngredients();
          await success(this, data.message);
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.deletingIngredient = false;
      }
    },
    mapIngredientForTable(item) {
      return {
        ...item,
        code_display: item.code || "-",
        ingredient_name: this.ingredientName(item),
        unit_label: this.selectedUnitLabel(item.unit_id, item),
        vendor_name: this.selectedSupplierName(item.vendor_id),
        active_label: item.is_active ? this.$t("active") : this.$t("inactive"),
        current_stock_display: this.stockUnitDisplay(item),
        minimum_stock_display: this.numberText(item.minimum_stock),
      };
    },
    // "10 Pack / 1 gram" for the current stock, using the ingredient's restock
    // units. Units are the ones a restock conversion mentions, but the factor
    // may travel through usage rows too — same rule the restock screen applies.
    // Falls back to the base amount when there is nothing to convert into.
    stockUnitDisplay(item) {
      const rows = item.unit_conversions || [];
      const baseUnitId = Number(item.unit_id);
      const baseLabel = this.selectedUnitLabel(baseUnitId, item);

      const restockUnitIds = new Set();
      rows.forEach((row) => {
        if (row.purpose !== "restock") return;
        restockUnitIds.add(Number(row.from_unit_id));
        restockUnitIds.add(Number(row.to_unit_id));
      });
      restockUnitIds.delete(baseUnitId);

      const units = Array.from(restockUnitIds).map((unitId) => ({
        label: this.selectedUnitLabel(unitId),
        factor: conversionFactor(rows, unitId, baseUnitId, [
          "restock",
          "usage",
        ]),
      }));

      return stockUnitText(item.current_stock, units, baseLabel, (value) =>
        this.numberText(value)
      );
    },
    mapIngredientToForm(item = {}) {
      return {
        id: item.id || null,
        vendor_id: item.vendor_id || null,
        code: item.code || "",
        name_la: item.name_la || "",
        name_en: item.name_en || "",
        name_th: item.name_th || "",
        name_cn: item.name_cn || "",
        unit_id: item.unit_id || null,
        unit_name: item.unit_name || null,
        unit_name_en: item.unit_name_en || null,
        unit_conversions: item.unit_conversions || [],
        current_stock: this.numberOrZero(item.current_stock),
        minimum_stock: this.numberOrZero(item.minimum_stock),
        unit_cost: this.numberOrZero(item.unit_cost),
        is_active: Number(item.is_active) === 1,
        bom_items_count: item.bom_items_count || 0,
        bom_stock_logs_count: item.bom_stock_logs_count || 0,
      };
    },
    ingredientName(item) {
      const locale = this.$i18n.locale;
      if (locale === "en" && item.name_en) return item.name_en;
      if (locale === "th" && item.name_th) return item.name_th;
      if ((locale === "cn" || locale === "ch") && item.name_cn) {
        return item.name_cn;
      }
      return (
        item.name_la || item.name_en || item.name_th || item.name_cn || "-"
      );
    },
    unitLabel(unit) {
      if (!unit) return "-";
      const locale = this.$i18n.locale;
      const name = locale === "en" && unit.name_en ? unit.name_en : unit.name;
      return name || "-";
    },
    selectedUnitLabel(unitId, item = null) {
      const unit = this.unitOptions.find((entry) => entry.id === unitId);
      if (unit) return unit.label;
      if (item?.unit_name || item?.unit_name_en) {
        return this.$i18n.locale === "en" && item.unit_name_en
          ? item.unit_name_en
          : item.unit_name;
      }
      return "-";
    },
    // Conversion rows embed their units; fall back to the fetched unit list.
    conversionUnitLabel(unit, unitId) {
      if (unit) return this.unitLabel(unit);
      return this.selectedUnitLabel(unitId);
    },
    selectedSupplierName(vendorId) {
      if (!vendorId) return "-";
      const supplier = this.suppliers.find((entry) => entry.id === vendorId);
      return supplier ? supplier.name : "-";
    },
    bomUsage(item) {
      return (
        Number(item.bom_items_count || 0) +
        Number(item.bom_stock_logs_count || 0)
      );
    },
    numberOrZero(value) {
      if (value === null || value === undefined || value === "") return 0;
      return Number(value);
    },
    numberText(value) {
      return this.numberOrZero(value).toLocaleString();
    },
  },
};
</script>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.ingredients-shell {
  border-color: #e8eef6;
}

.ingredients-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.ingredients-title {
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
}

.ingredients-subtitle {
  margin-top: 8px;
  color: #64748b;
}

.ingredients-header-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.ingredients-action-btn {
  height: 44px !important;
  padding: 0 20px !important;
  font-weight: 700;
}

.ingredients-filters-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
}

.ingredients-filter-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ingredients-filter-label {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.ingredients-filter-divider {
  width: 1px;
  align-self: stretch;
  background: #e2e8f0;
}

.ingredients-chip-group ::v-deep .v-slide-group__content {
  gap: 8px;
}

.filter-chip {
  transition: all 0.2s ease;
  background: #f1f5f9 !important;
  color: #475569 !important;
  font-weight: 600;
}

.filter-chip-active {
  background: #1d5fc2 !important;
  color: #fff !important;
}

.ingredients-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
}

.ingredients-search {
  flex: 1 1 320px;
  position: relative;
  display: flex;
  align-items: center;
}

.ingredients-search-icon {
  position: absolute;
  left: 16px;
  color: #94a3b8;
}

.ingredients-search-input {
  width: 100%;
  height: 46px;
  padding: 0 42px;
  border: 1.5px solid #e2e8f0;
  border-radius: 999px;
  background: #fff;
  font-size: 14px;
  color: #0f172a;
  outline: none;
  transition: border-color 0.2s ease;
}

.ingredients-search-input::placeholder {
  color: #94a3b8;
}

.ingredients-search-input:focus {
  border-color: #438ef2;
}

.ingredients-search-clear {
  position: absolute;
  right: 8px;
}

.ingredients-supplier-select {
  flex: none;
  width: 220px;
}

.ingredients-supplier-select ::v-deep .v-input__slot {
  background: #f1f5f9 !important;
  border-radius: 999px !important;
  min-height: 46px;
}

.ingredients-supplier-select ::v-deep fieldset {
  border-color: transparent !important;
}

.ingredients-supplier-select ::v-deep .v-input__append-inner {
  align-self: center !important;
  margin-top: 0 !important;
}

/* ---- data table modernization (shared DataTable component) ---- */
.ingredients-table-wrap ::v-deep .v-card {
  border: 1px solid #e8eef6;
  border-radius: 16px !important;
  box-shadow: none;
}

.ingredients-table-wrap ::v-deep table.v-table thead th {
  background: #f8fafc;
  color: #64748b !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid #e8eef6 !important;
  height: 48px !important;
}

.ingredients-table-wrap ::v-deep table.v-table tbody td {
  color: #1f2937;
  font-size: 13.5px;
  height: 60px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.ingredients-table-wrap ::v-deep table.v-table tbody tr:hover {
  background: #f8fbff !important;
}

.ingredients-table-wrap ::v-deep table.v-table tbody tr:last-child td {
  border-bottom: none !important;
}

.ingredients-table-wrap ::v-deep .action-icon-btn,
.ingredients-table-wrap ::v-deep .v-data-table td .v-btn.v-btn--fab {
  border-radius: 8px !important;
  background: #f4f7fb !important;
  box-shadow: none !important;
}

.ingredients-table-wrap ::v-deep .v-data-table td .v-btn.v-btn--fab:hover {
  background: #eaf1fc !important;
}

.ingredients-table-wrap ::v-deep .v-data-footer {
  color: #7f8794;
  font-size: 12px;
  border-top: 1px solid #e8eef6 !important;
}

/* ---- skeleton ---- */
.skeleton-block {
  border-radius: 8px;
  background: linear-gradient(90deg, #eef1f5 25%, #f7f9fb 37%, #eef1f5 63%);
  background-size: 400% 100%;
  animation: ingredients-list-skeleton-shimmer 1.4s ease infinite;
}

@keyframes ingredients-list-skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

.skeleton-title-block {
  width: 180px;
  height: 28px;
}

.skeleton-btn {
  width: 116px;
  height: 44px;
  border-radius: 999px;
}

.ingredients-skeleton-toolbar,
.ingredients-skeleton-filters,
.ingredients-skeleton-chips,
.ingredients-skeleton-actions {
  align-items: center;
  display: flex;
  gap: 12px;
}

.ingredients-skeleton-toolbar,
.ingredients-skeleton-filters {
  flex-wrap: wrap;
  justify-content: space-between;
}

.ingredients-skeleton-chips,
.ingredients-skeleton-actions {
  flex-wrap: wrap;
}

@media (max-width: 960px) {
  .ingredients-header {
    flex-direction: column;
  }
}

@media (max-width: 600px) {
  .ingredients-title {
    font-size: 24px;
  }

  .ingredients-supplier-select {
    width: 100%;
  }

  .ingredients-filters-row {
    flex-direction: column;
    align-items: stretch;
  }

  .ingredients-filter-divider {
    width: 100%;
    height: 1px;
  }

  .ingredients-skeleton-toolbar,
  .ingredients-skeleton-filters {
    align-items: stretch;
    flex-direction: column;
  }
}

/* ---- ingredient detail dialog ---- */
.ingredient-detail {
  padding: 4px 2px;
}

.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.detail-header-name {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 20px;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 14px;
  margin-bottom: 20px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.detail-label {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.detail-value {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  overflow-wrap: anywhere;
}

.detail-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 10px;
}

.detail-stats-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 20px;
}

.detail-stat {
  border-radius: 12px;
  background: #eaf3ff;
  padding: 10px 8px;
  text-align: center;
}

.detail-stat-value {
  font-size: 14px;
  font-weight: 700;
  color: #1d4ed8;
  line-height: 1.3;
}

.detail-stat-label {
  margin-top: 4px;
  font-size: 11px;
  color: #64748b;
}

.detail-empty {
  color: #94a3b8;
  font-size: 13px;
}

.conversion-row-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.conversion-row-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 10px;
}

.conversion-row-unit {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
}

.conversion-row-arrow {
  color: #94a3b8;
}

.conversion-row-purpose {
  margin-left: auto;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.conversion-row-purpose--restock {
  background: #eaf3ff;
  color: #1d4ed8;
}

.conversion-row-purpose--usage {
  background: #fff4e5;
  color: #a95c05;
}

@media (max-width: 480px) {
  .detail-grid,
  .detail-stats-row {
    grid-template-columns: 1fr;
  }

  .conversion-row-card {
    flex-wrap: wrap;
  }

  .conversion-row-purpose {
    margin-left: 0;
  }
}

/* ---- Dark Mode Overrides ---- */
.theme--dark .ingredients-shell {
  border-color: rgba(255, 255, 255, 0.08) !important;
  background: #1e1e2d !important;
}

.theme--dark .ingredients-title,
.theme--dark .detail-header-name,
.theme--dark .detail-section-title {
  color: #ffffff;
}

.theme--dark .ingredients-filter-divider {
  background: rgba(255, 255, 255, 0.1);
}

.theme--dark .filter-chip {
  background: #262638 !important;
  color: #cbd5e1 !important;
}

.theme--dark .filter-chip-active {
  background: rgba(154, 140, 240, 0.18) !important;
  color: #c4b5fd !important;
}

.theme--dark .ingredients-search-input {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.12);
  color: #f1f5f9;
}

.theme--dark .ingredients-supplier-select ::v-deep .v-input__slot {
  background: #262638 !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
}

.theme--dark .ingredients-supplier-select ::v-deep .v-select__selection,
.theme--dark .ingredients-supplier-select ::v-deep input {
  color: #f1f5f9 !important;
}

.theme--dark .ingredients-table-wrap ::v-deep .v-card {
  border-color: rgba(255, 255, 255, 0.08);
  background: #1e1e2d !important;
}

.theme--dark .ingredients-table-wrap ::v-deep table.v-table thead th {
  background: transparent !important;
  background-color: transparent !important;
  color: #94a3b8 !important;
  border-bottom-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .ingredients-table-wrap ::v-deep table.v-table tbody td {
  color: #f1f5f9;
  border-bottom-color: rgba(255, 255, 255, 0.06) !important;
}

.theme--dark .ingredients-table-wrap ::v-deep table.v-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.03) !important;
}

.theme--dark .ingredients-table-wrap ::v-deep .action-icon-btn,
.theme--dark .ingredients-table-wrap ::v-deep .v-data-table td .v-btn.v-btn--fab {
  background: #262638 !important;
}

.theme--dark .ingredients-table-wrap ::v-deep .v-data-footer {
  color: #94a3b8;
  border-top-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .detail-grid,
.theme--dark .conversion-row-card {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .detail-value,
.theme--dark .conversion-row-unit {
  color: #f1f5f9;
}

.theme--dark .detail-stat {
  background: rgba(129, 140, 248, 0.14);
}

.theme--dark .detail-stat-value {
  color: #c4b5fd;
}
</style>
